import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { CultureService } from '@app/services/culture.service';
import { Environment } from '@environments/interface.environment';
import { FetchOptions, HttpClientImpl } from '@pnp/common';
import { ILabel, ILabelData, ITerm, ITermData, Session } from '@pnp/sp-taxonomy';
import { from, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TaxonomyService {

  private readonly taxonomy: Session;
  private readonly labelsPropertyKey = 'Labels';
  private readonly parentPropertyKey = 'Parent';
  private readonly childItemsPropertyKey = '_Child_Items_';
  private readonly simpleLinkPropertyKey = '_Sys_Nav_SimpleLinkUrl';

  constructor(
    private http: HttpClient,
    private cultureService: CultureService,
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment) {
    this.taxonomy = new Session(this.environment.coreWorkplaceSiteUrl);
    this.taxonomy.setup({
      sp: {
        fetchClientFactory: () => {
          return new CustomSPHttpClient(this.http);
        }
      }
    });
  }

  getAmountOfChangesSince(date: Date): Observable<number> {
    return from(
      this.taxonomy
        .getDefaultSiteCollectionTermStore()
        .getChanges({ StartTime: date.toISOString() })
        .then(changes => {
          return changes.length;
        }));
  }

  getTermByIdWithFullHierarchy(termId: string): Observable<SimplifiedTerm> {
    const selects = ['Id',
                     'Name',
                     'IsRoot',
                     'TermsCount',
                     this.labelsPropertyKey,
                     'LastModifiedDate',
                     'IsAvailableForTagging',
                     'IsDeprecated',
                     'PathOfTerm',
                     'LocalCustomProperties',
                     this.parentPropertyKey];
    return this.cultureService.getLcidForCurrentLocale()
      .pipe(
        mergeMap(lcid => {
          return from(
            this.taxonomy
              .getDefaultSiteCollectionTermStore()
              .getTermById(termId)
              .select(...selects)
              .get()
              .then(term => Promise.all([term.termSet.terms.select(...selects).get(), Promise.resolve(term)]))
              .then(([termsData, termData]) => {
                return this.buildSimplifiedTermRecursively(termData, lcid, termsData);
              }));
        })
      );
  }

  getTermSet(termSetId: string, filterDeprecated = false): Observable<Array<SimplifiedTerm>> {
    return this.cultureService.getLcidForCurrentLocale()
      .pipe(
        mergeMap(lcid => {
          return from(
            this.taxonomy
              .getDefaultSiteCollectionTermStore()
              .getTermSetById(termSetId)
              .terms
              .select(
                'Id',
                'Name',
                'IsRoot',
                'TermsCount',
                this.labelsPropertyKey,
                'LastModifiedDate',
                'IsAvailableForTagging',
                'IsDeprecated',
                'PathOfTerm',
                'LocalCustomProperties',
                this.parentPropertyKey
              )
              .get()
              .then(termsData => {
                return termsData
                  .map((termData, index, allTermsData) => {
                    if (termData.IsRoot && !(filterDeprecated && termData.IsDeprecated)) {
                      return this.buildSimplifiedTermRecursively(termData, lcid, allTermsData, filterDeprecated);
                    }
                  })
                  .filter(t => t); // get rid of nulls
              }));
        })
      );
  }

  // get all terms in the termset in a flat list
  getTermSetFlat(termSetId: string): Observable<Array<SimplifiedTerm>> {
    return this.cultureService.getLcidForCurrentLocale()
      .pipe(
        mergeMap(lcid => {
          return from(
            this.taxonomy
              .getDefaultSiteCollectionTermStore()
              .getTermSetById(termSetId)
              .terms
              .select(
                'Id',
                'Name',
                'IsRoot',
                'TermsCount',
                this.labelsPropertyKey,
                'LastModifiedDate',
                'IsAvailableForTagging',
                'IsDeprecated',
                'PathOfTerm',
                'LocalCustomProperties',
                this.parentPropertyKey
              )
              .get()
              .then(termsData => {
                return termsData
                  .map((termData, index, allTermsData) => {
                    return this.buildSimplifiedTermFlat(termData, lcid, allTermsData);
                  })
                  .filter(t => t); // get rid of nulls
              }));
        })
      );
  }

  // build a simplifiedTerm without populating the children (.Terms property)
  private buildSimplifiedTermFlat(termData: (ITerm & ITermData), languageCode: number, allTermsData: (ITerm & ITermData)[]): SimplifiedTerm {
    let labels = new Array<ILabel & ILabelData>();
    if (termData[this.labelsPropertyKey] && termData[this.labelsPropertyKey].hasOwnProperty(this.childItemsPropertyKey)) {
      labels = termData[this.labelsPropertyKey][this.childItemsPropertyKey];
    }
    const termLabel = labels.find(l => l.Language === languageCode);
    return this.buildSimplifiedTerm(termData, termLabel, labels);

  }

  private buildSimplifiedTermRecursively(termData: (ITerm & ITermData), languageCode: number, allTermsData: (ITerm & ITermData)[], filterDeprecated = false): SimplifiedTerm {
    let labels = new Array<ILabel & ILabelData>();
    if (termData[this.labelsPropertyKey] && termData[this.labelsPropertyKey].hasOwnProperty(this.childItemsPropertyKey)) {
      labels = termData[this.labelsPropertyKey][this.childItemsPropertyKey];
    }
    const termLabel = labels.find(l => l.Language === languageCode);

    if (termData.TermsCount > 0) {
      const childTerms = allTermsData.filter(term => term[this.parentPropertyKey] && term[this.parentPropertyKey].Id === termData.Id);
      const simplifiedTerm = this.buildSimplifiedTerm(termData, termLabel, labels);
      simplifiedTerm.Terms = childTerms.filter(item => !(filterDeprecated && item.IsDeprecated)).map(item => {
        const term = this.buildSimplifiedTermRecursively(item, languageCode, allTermsData, filterDeprecated);
        term.ParentTermGuid = simplifiedTerm.Guid;
        return term;
      }).sort((a, b) => a?.Name?.localeCompare(b?.Name));
      return simplifiedTerm;
    } else {
      return this.buildSimplifiedTerm(termData, termLabel, labels);
    }
  }

  private buildSimplifiedTerm(term: ITerm & ITermData, termLabel: ILabel & ILabelData, labels: (ILabel & ILabelData)[]): SimplifiedTerm {

    return new SimplifiedTerm({
      Guid: term.Id.replace('/Guid(', '').replace(')/', ''), // GUIDs come in this format: /Guid(89243421-026c-41de-80fd-03a09d0d4805)/
      Name: termLabel ? termLabel.Value : term.Name,
      InternalName: term.Name,
      Labels: labels.map(label => {
        return {
          Language: label.Language,
          Name: label.Value
        };
      }),
      TermCount: term.TermsCount,
      LastModified: this.getDateFromAspNetFormat(term.LastModifiedDate),
      Url: this.getUrlForTerm(term),
      FriendlyUrl: this.getFriendlyUrl(term),
      IsRoot: term.IsRoot,
      LocalCustomProperties: term.LocalCustomProperties,
      IsAvailableForTagging: term.IsAvailableForTagging,
      IsDeprecated: term.IsDeprecated,
      ParentTermGuid: term[this.parentPropertyKey] ? term[this.parentPropertyKey].Id.replace('/Guid(', '').replace(')/', '') : ''
    });
  }

  // Dates come in this format /Date(1486309038000)/
  private getDateFromAspNetFormat(date: string) {
    const dateTicks = /-?\d+/.exec(date);
    return new Date(Number.parseInt(dateTicks[0], 10));
  }

  private getFriendlyUrl(term: ITermData) {
    return '/' + term.PathOfTerm.replace(';', '/').replace(' ', '-').replace('＆', '&');
  }

  private getUrlForTerm(term: ITermData) {
    const customProps = term.LocalCustomProperties;
    if (customProps && customProps.hasOwnProperty(this.simpleLinkPropertyKey)) {
      return customProps[this.simpleLinkPropertyKey];
    }
    return null;
  }

}

export class CustomSPHttpClient implements HttpClientImpl {

  private defaultHttpHeaders = new HttpHeaders()
    .append('Accept', 'application/json;odata=verbose')
    .append('Content-type', 'application/json;odata=verbose;charset=utf-8');

  constructor(private http: HttpClient) { }

  fetch(url: string, options: FetchOptions): Promise<Response> {
    let localHttpHeaders = new HttpHeaders();
    const headers = options.headers as Headers;
    if (headers) {
      headers.forEach((val, key) => {
        localHttpHeaders = localHttpHeaders.append(key, val);
      });
    }

    if (localHttpHeaders.keys().length === 0) {
      localHttpHeaders = this.defaultHttpHeaders;
    }

    return this.http
      .request(
        options.method,
        url,
        {
          body: options.body,
          headers: localHttpHeaders,
          observe: 'response',
          responseType: 'text'
        })
      .pipe(
        map(response =>
          new Response(
            response.body,
            {
              status: response.status,
              statusText: response.statusText
            })))
      .toPromise();
  }

}
